/* eslint react-hooks/exhaustive-deps: "warn" */

import { useEffect, useRef } from 'react';

import cn from 'classnames';

import { Flag } from 'ui/atoms/Flag/Flag';
import { Icon } from 'ui/atoms/Icon/Icon';
import { Image } from 'ui/atoms/Image';
import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';

import { MetricsTooltip } from 'features/company-data/atoms/MetricsTooltip/MetricsTooltip';

import styles from './CompanyDataExplorer.module.scss';

import { CompanyDataExplorerProps, ExplorerVisual } from './CompanyDataExplorer.types';

const renderVisual = (visual: ExplorerVisual, flagClassName: string, iconClassName: string) => {
  if (visual.type === 'flag') {
    return <Flag code={visual.code} className={flagClassName} />;
  }

  return <Icon type={visual.iconType} className={iconClassName} />;
};

export const CompanyDataExplorer = ({
  title,
  description,
  searchFields,
  searchAction,
  allItems,
  columns,
  selectedKey,
  onSelectKey,
  globalMetrics,
  tooltipFallbackTitle,
  tooltipMeta,
  className,
}: CompanyDataExplorerProps) => {
  const sectionRef = useRef<HTMLElement>(null);
  const hasInteractedRef = useRef(false);

  const activeItem = allItems.find((item) => item.key === selectedKey);

  useEffect(() => {
    const node = sectionRef.current;
    if (!node) {
      return;
    }

    const markInteracted = () => {
      hasInteractedRef.current = true;
    };

    node.addEventListener('pointerdown', markInteracted);
    node.addEventListener('keydown', markInteracted);

    return () => {
      node.removeEventListener('pointerdown', markInteracted);
      node.removeEventListener('keydown', markInteracted);
    };
  }, []);

  useEffect(() => {
    if (allItems.length === 0) {
      return;
    }

    const id = setInterval(() => {
      if (hasInteractedRef.current) {
        clearInterval(id);
        return;
      }

      const idx = allItems.findIndex((item) => item.key === selectedKey);
      const next = allItems[(idx + 1) % allItems.length];
      onSelectKey(next.key);
    }, 3000);

    return () => {
      clearInterval(id);
    };
  }, [allItems, selectedKey, onSelectKey]);

  const metrics = activeItem ? activeItem.metrics : globalMetrics;
  const tooltipTitle = activeItem ? activeItem.name : tooltipFallbackTitle;
  const tooltipFlagCode = activeItem?.visual.type === 'flag' ? activeItem.visual.code : undefined;
  const tooltipIconType = activeItem?.visual.type === 'icon' ? activeItem.visual.iconType : undefined;

  return (
    <section ref={sectionRef} className={cn(styles.section, className)}>
      <div className={styles.container}>
        <div className={styles.header}>
          <TitleBlock title={title} description={description} />

          {(searchFields || searchAction) && (
            <div className={styles.search}>
              {searchFields && <div className={styles.fields}>{searchFields}</div>}

              {searchAction && <div className={styles.searchAction}>{searchAction}</div>}
            </div>
          )}
        </div>

        <div className={styles.world}>
          <div className={styles.countries}>
            {columns.map((column) => (
              <div key={column.key} className={styles.column}>
                {column.label && <h3 className={styles.columnHeading}>{column.label}</h3>}

                <ul className={styles.list}>
                  {column.items.map((item) => (
                    <li key={item.key}>
                      <button
                        type="button"
                        className={`${styles.item} ${item.key === selectedKey ? styles.item_active : ''}`}
                        onClick={() => onSelectKey(item.key)}
                      >
                        {renderVisual(item.visual, styles.flag, styles.itemIcon)}

                        <span className={styles.itemName}>{item.name}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className={styles.globe} aria-hidden="true">
            <Image
              className={styles.globeImage}
              src="/img/company-data-globe-illustration.svg"
              alt="company-data-globe-illustration"
              loading="lazy"
              width={720}
              height={722}
            />

            <MetricsTooltip
              flagCode={tooltipFlagCode}
              iconType={tooltipIconType}
              title={tooltipTitle}
              meta={tooltipMeta}
              metrics={metrics}
            />
          </div>
        </div>
      </div>
    </section>
  );
};
